import React, { useState, useEffect, useMemo, useRef } from "react";
import { ChevronDown, Upload, Plus, X, Edit2, Trash2 } from "lucide-react";
import { TemplateModule } from "../../../../views/marketing-pages/templates";
import { FieldLabel, CardShell } from "./card-handler";
import { toast } from "sonner";

type MediaUploadRequest = (data: FormData) => Promise<any[]>;

interface ReplyButton {
  id: string;
  label: string;
}

interface FetchAudienceCardProps {
  config: Record<string, any>;
  onUpdate: (config: Record<string, any>) => void;
  onDelete: () => void;
  onReset: () => void;
  broadcastExcelUpload?: MediaUploadRequest;
  journeyId?: string | null;
  templates?: TemplateModule[];
  bare?: boolean;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function extractVariableSlots(content: string): number[] {
  const found = new Set<number>();
  for (const m of content.matchAll(/\{\{(\d+)\}\}/g)) {
    found.add(Number(m[1]));
  }
  return Array.from(found).sort((a, b) => a - b);
}

function buildFinalMessage(content: string, variables: Record<number, string>): string {
  return content.replace(/\{\{(\d+)\}\}/g, (_, n) => {
    const value = variables[Number(n)];
    return value ? value.replace(/^{{/, "").replace(/}}$/, "") : `{{${n}}}`;
  });
}

const DEFAULT_VARIABLE_MAPPINGS: Record<number, string> = {
  1: "{{first_name}}",
  2: "{{last_name}}",
  3: "{{email}}",
  4: "{{phone}}",
  5: "{{source}}",
};

const DEFAULT_REPLY_BUTTONS = [
  { id: "btn_yes", label: "Yes" },
  { id: "btn_no", label: "No" },
  { id: "btn_reschedule", label: "Reschedule" },
];

// ─── Reply Buttons Component ───────────────────────────────────────────────────
const ReplyButtons: React.FC<{
  buttons: ReplyButton[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onAdd: () => void;
  onRemove: (id: string) => void;
  onLabelChange: (id: string, label: string) => void;
}> = ({ buttons, selectedId, onSelect, onAdd, onRemove, onLabelChange }) => {
  const [, setEditingId] = useState<string | null>(null);

  const isDefaultButton = (id: string) => 
    ["btn_yes", "btn_no", "btn_reschedule"].includes(id);

  return (
    <div className="mt-4">
      <FieldLabel>Quick Reply Buttons</FieldLabel>
      <div className="space-y-2 mt-2">
        {buttons.map((btn) => {
          const isDefault = isDefaultButton(btn.id);
          return (
            <div
              key={btn.id}
              onClick={() => onSelect(btn.id)}
              className={`
                flex items-center justify-between rounded-xl px-4 py-2.5 cursor-pointer transition-all
                ${selectedId === btn.id
                  ? "bg-indigo-50 border border-indigo-200"
                  : "bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-indigo-200 hover:bg-indigo-50/40"
                }
              `}
            >
              <span className={`text-sm font-medium flex-1 ${selectedId === btn.id ? "text-gray-800 dark:text-gray-200" : "text-indigo-600"}`}>
                {btn.label}
              </span>

              {/* Show edit/delete only for custom buttons */}
              {!isDefault && (
                <div className="flex items-center gap-2 ml-2">
                  <button
                    onClick={(e) => { e.stopPropagation(); setEditingId(btn.id); }}
                    className="text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-indigo-500 transition-colors"
                  >
                    <Edit2 size={12} />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); onRemove(btn.id); }}
                    className="text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-red-400 transition-colors"
                  >
                    <X size={12} />
                  </button>
                </div>
              )}

              {/* Blue connection dot */}
              <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ml-3 flex items-center justify-center transition-all
                ${selectedId === btn.id ? "border-indigo-500 bg-white dark:bg-gray-900" : "border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900"}`}
              >
                {selectedId === btn.id && (
                  <div className="w-2 h-2 rounded-full bg-indigo-500" />
                )}
              </div>
            </div>
          );
        })}
      </div>

      {buttons.length < 5 && (
        <button
          onClick={onAdd}
          className="mt-2 w-full flex items-center justify-center gap-1.5 text-xs text-indigo-500 hover:text-indigo-700 border border-dashed border-indigo-300 rounded-xl py-2 hover:border-indigo-400 transition-colors"
        >
          <Plus size={13} /> Add Button
        </button>
      )}
    </div>
  );
};

// ─── Main Card ────────────────────────────────────────────────────────────────
export const FetchAudienceCard: React.FC<FetchAudienceCardProps> = ({
  config,
  onUpdate,
  onDelete,
  onReset,
  broadcastExcelUpload,
  journeyId,
  templates = [],
  bare,
}) => {
  const [uploading, setUploading] = useState(false);

  const update = (patch: Record<string, any>) => onUpdate({ ...config, ...patch });

  const availableTemplates = templates.filter(t =>
    !t.channel || t.channel.toLowerCase() === "whatsapp"
  );

  const selectedTemplate = useMemo(() => {
    const id = config.templateId ?? config.template_id ?? "";
    const name = config.templateName ?? config.template ?? config.template_name ?? "";
    return availableTemplates.find(t =>
      (id && t.id === id) || (name && t.template_name === name)
    ) ?? null;
  }, [availableTemplates, config]);

  const variableSlots = selectedTemplate ? extractVariableSlots(selectedTemplate.content ?? "") : [];
  const variables = config.variables ?? {};

  // Reply Buttons State
  const [replyButtons, setReplyButtons] = useState<ReplyButton[]>(
    config.replyButtons?.length ? config.replyButtons : DEFAULT_REPLY_BUTTONS
  );
  const [selectedButtonId, setSelectedButtonId] = useState<string | null>(
    config.selectedButtonId ?? null
  );

  const syncButtons = (buttons: ReplyButton[], selId: string | null) => {
    setReplyButtons(buttons);
    setSelectedButtonId(selId);
    update({ replyButtons: buttons, selectedButtonId: selId });
  };

  const addButton = () => {
    const id = `btn_${Date.now()}`;
    const newBtn: ReplyButton = { id, label: `Option ${replyButtons.length + 1}` };
    syncButtons([...replyButtons, newBtn], selectedButtonId);
  };

  const removeButton = (id: string) => {
    const next = replyButtons.filter(b => b.id !== id);
    const newSel = selectedButtonId === id ? (next[0]?.id ?? null) : selectedButtonId;
    syncButtons(next, newSel);
  };

  const updateLabel = (id: string, label: string) => {
    syncButtons(replyButtons.map(b => b.id === id ? { ...b, label } : b), selectedButtonId);
  };

  // Auto-fill variables
  useEffect(() => {
    if (!selectedTemplate) return;
    const slots = extractVariableSlots(selectedTemplate.content ?? "");
    const saved = config.variables ?? {};
    const newVars: Record<number, string> = { ...saved };

    slots.forEach(slot => {
      if (!newVars[slot] || newVars[slot] === "") {
        newVars[slot] = DEFAULT_VARIABLE_MAPPINGS[slot] || `{{${slot}}}`;
      }
    });

    if (JSON.stringify(newVars) !== JSON.stringify(saved)) {
      onUpdate({ ...config, variables: newVars });
    }
  }, [selectedTemplate?.id]);

  const setVariableValue = (index: number, value: string) => {
    onUpdate({ ...config, variables: { ...variables, [index]: value } });
  };

  const handleTemplateChange = (templateId: string) => {
    const tpl = availableTemplates.find(t => t.id === templateId) ?? null;
    const slots = tpl ? extractVariableSlots(tpl.content ?? "") : [];
    const newVariables: Record<number, string> = {};

    slots.forEach(slot => {
      newVariables[slot] = DEFAULT_VARIABLE_MAPPINGS[slot] || `{{${slot}}}`;
    });

    // Reset the operator's Yes/No/Reschedule radio set whenever the
    // template changes — so picking appointment_reschedule shows the
    // template's REAL buttons (Tomorrow / This week / Next week) instead
    // of leaving the old appointment_reminder buttons stuck on screen.
    // Falls back to a hardcoded map when the CRM /templates/ endpoint
    // doesn't return quick_reply_buttons. (Joseph 06-29).
    const FALLBACK_BUTTONS_BY_TPL: Record<string, string[]> = {
      appointment_reminder: ["YES", "NO", "Reschedule"],
      appointment_reschedule: ["Tomorrow", "This week", "Next week"],
      appointment_cancel_reason: ["Different time slot", "Not interested", "Need assistance"],
      appointment_missed: ["Reschedule", "Cancel"],
      feedback_request: ["Good", "Average", "Poor"],
    };
    let nextButtons: ReplyButton[] = [];
    const tplButtons = tpl?.quick_reply_buttons ?? [];
    if (tplButtons.length) {
      nextButtons = tplButtons.map((b: any, i: number) => ({
        id: `btn_${b.label?.toLowerCase()?.replace(/\s+/g, "_") || i}`,
        label: b.label ?? `Option ${i + 1}`,
      }));
    } else if (tpl?.template_name && FALLBACK_BUTTONS_BY_TPL[tpl.template_name]) {
      nextButtons = FALLBACK_BUTTONS_BY_TPL[tpl.template_name].map((label, i) => ({
        id: `btn_${label.toLowerCase().replace(/\s+/g, "_")}_${i}`,
        label,
      }));
    } else {
      // Unknown template — keep the standard YES/NO/Reschedule defaults
      // so the operator can still configure branches.
      nextButtons = [...DEFAULT_REPLY_BUTTONS];
    }
    setReplyButtons(nextButtons);
    setSelectedButtonId(null);

    onUpdate({
      ...config,
      templateId: tpl?.id ?? "",
      templateName: tpl?.template_name ?? "",
      language: tpl?.language ?? "en_US",
      variables: newVariables,
      replyButtons: nextButtons,
      selectedButtonId: null,
    });
  };

  const getDisplayValue = (value: string) => value?.replace(/^{{(.*)}}$/, "$1") || "";

  // ── Manual entry table (replaces CSV upload per Joseph 06-29) ────────────
  // Static headers; each row validated per cell; submit converts rows
  // to CSV in-memory and posts via the SAME broadcastExcelUpload call
  // (so backend stays unchanged — it just sees a CSV with the same
  // columns it always did).
  type Row = {
    phone: string;
    first_name: string;
    last_name: string;
    email: string;
    appointment_date: string;  // dd-mm-yyyy
    appointment_time: string;  // HH:MM
  };
  const EMPTY_ROW: Row = {
    phone: "", first_name: "", last_name: "", email: "",
    appointment_date: "", appointment_time: "",
  };
  // 5 default empty rows so the table behaves like a familiar spreadsheet —
  // operator can fire 5 customers without ever hitting "Add row".
  const [rows, setRows] = useState<Row[]>(
    Array.isArray(config.manualRows) && config.manualRows.length
      ? config.manualRows
      : Array.from({ length: 5 }, () => ({ ...EMPTY_ROW }))
  );

  // A row is "touched" only if the operator typed anything into any cell.
  // Completely empty rows are placeholders — they don't get validated red,
  // they just get filtered out at submit time. Only TOUCHED rows that fail
  // validation render with red borders + block submit.
  const isRowTouched = (r: Row) =>
    !!(r.phone || r.first_name || r.last_name ||
       r.email || r.appointment_date || r.appointment_time);

  const rowErrors = useMemo(() => {
    return rows.map((r) => {
      if (!isRowTouched(r)) {
        // Empty placeholder row — no red anywhere.
        return {
          phone: false, first_name: false, last_name: false,
          email: false, appointment_date: false, appointment_time: false,
          _empty: true,
        };
      }
      return {
        phone:
          !r.phone ? true
          : !/^\+[1-9]\d{6,14}$/.test(r.phone.trim()),
        first_name: !r.first_name?.trim(),
        last_name: false,                       // optional
        email:
          !r.email ? false
          : !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(r.email.trim()),
        appointment_date:
          !r.appointment_date ? true
          : !/^\d{2}-\d{2}-\d{4}$/.test(r.appointment_date.trim()),
        appointment_time:
          !r.appointment_time ? true
          : !/^\d{2}:\d{2}$/.test(r.appointment_time.trim()),
        _empty: false,
      };
    });
  }, [rows]);

  const touchedRowCount = rows.filter(isRowTouched).length;
  const allTouchedRowsValid = rowErrors.every(
    (e) => e._empty || !Object.entries(e).filter(([k]) => k !== "_empty")
      .some(([, v]) => v === true)
  );
  // Need at least 1 touched row + all touched rows must validate.
  const canSubmit = touchedRowCount > 0 && allTouchedRowsValid;

  const setCell = (idx: number, field: keyof Row, value: string) => {
    const next = rows.map((r, i) => (i === idx ? { ...r, [field]: value } : r));
    setRows(next);
    update({ manualRows: next });
  };
  const addRow = () => {
    const next = [...rows, { ...EMPTY_ROW }];
    setRows(next);
    update({ manualRows: next });
  };
  const removeRow = (idx: number) => {
    const next = rows.length === 1 ? [{ ...EMPTY_ROW }] : rows.filter((_, i) => i !== idx);
    setRows(next);
    update({ manualRows: next });
  };

  const submitManualRows = async () => {
    if (!broadcastExcelUpload) {
      toast.error("Upload handler not wired");
      return;
    }
    if (!canSubmit) {
      toast.error(
        touchedRowCount === 0
          ? "Enter at least one customer row before submitting"
          : "Fix the highlighted fields before submitting"
      );
      return;
    }
    // Strip empty placeholder rows from the submission.
    const submittedRows = rows.filter(isRowTouched);

    // Build a CSV blob matching the schema the backend already expects.
    const header = "phone,first_name,last_name,email,appointment_date,appointment_time\n";
    const body = submittedRows.map(r =>
      [r.phone, r.first_name, r.last_name, r.email, r.appointment_date, r.appointment_time]
        .map(v => `"${(v ?? "").replace(/"/g, '""')}"`)
        .join(",")
    ).join("\n");
    const blob = new Blob([header + body + "\n"], { type: "text/csv" });
    const file = new File([blob], "manual-entry.csv", { type: "text/csv" });

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    if (journeyId) formData.append("journey_id", journeyId);
    formData.append("template_name", config.templateName ?? "");

    const [response] = await broadcastExcelUpload(formData);
    setUploading(false);

    if (response?.status === "error") {
      toast.error("Upload failed");
      return;
    }
    update({ uploadStatus: "submitted", manualRows: rows });
    toast.success(`Audience submitted (${submittedRows.length} customer${submittedRows.length === 1 ? "" : "s"})`);
  };

  return (
    <CardShell
      title="Fetch Audience"
      description="Fetches current date to identify attributes"
      onDelete={onDelete}
      onReset={onReset}
      bare={bare}
    >
        {/* Channel Selection — for now WhatsApp only; the dropdown
            reserves the slot for SMS/email channels later without
            having to restructure the card. (Joseph 2026-06-29) */}
        <div className="mb-4">
          <FieldLabel>Select Channel</FieldLabel>
          <div className="relative">
            <select
              value={config.channel ?? "WhatsApp"}
              onChange={(e) =>
                onUpdate({ ...config, channel: e.target.value })
              }
              className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm appearance-none bg-white dark:bg-gray-900 pr-8 focus:outline-none focus:ring-1 focus:ring-indigo-300"
            >
              <option value="WhatsApp">WhatsApp</option>
            </select>
            <ChevronDown size={16} className="absolute right-3 top-3 text-[#7B7B7B] font-semibold dark:text-gray-500 pointer-events-none" />
          </div>
        </div>

        {/* Template Selection */}
        <div className="mb-4">
          <FieldLabel>Select Template</FieldLabel>
          <div className="relative">
            <select
              value={selectedTemplate?.id ?? config.templateId ?? ""}
              onChange={(e) => handleTemplateChange(e.target.value)}
              className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm appearance-none bg-white dark:bg-gray-900 pr-8 focus:outline-none focus:ring-1 focus:ring-indigo-300"
            >
              <option value="">— choose template —</option>
              {availableTemplates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.template_name}
                </option>
              ))}
            </select>
            <ChevronDown size={16} className="absolute right-3 top-3 text-[#7B7B7B] font-semibold dark:text-gray-500 pointer-events-none" />
          </div>
        </div>

        {/* Excel-style manual entry table — per Joseph 06-29.
            Static header row at top with column labels. 5 default
            empty rows. Cells go red if invalid AND the row has been
            touched (so empty placeholders stay neutral). Submit only
            considers touched rows. */}
        <div className="mb-4">
          <FieldLabel>Customer list</FieldLabel>
          <p className="text-[10px] text-[#7B7B7B] font-semibold dark:text-gray-500 mb-2">
            Fill the table below. Phone in E.164 (+countrycode).
            Date dd-mm-yyyy. Time HH:MM (24h).
          </p>
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-x-auto">
            <table className="w-full text-[10px]" style={{ minWidth: "560px" }}>
              <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className="text-left text-[10px] font-semibold text-gray-600 dark:text-gray-300 px-2 py-1.5 w-6">#</th>
                  <th className="text-left text-[10px] font-semibold text-gray-600 dark:text-gray-300 px-2 py-1.5">Phone</th>
                  <th className="text-left text-[10px] font-semibold text-gray-600 dark:text-gray-300 px-2 py-1.5">First name</th>
                  <th className="text-left text-[10px] font-semibold text-gray-600 dark:text-gray-300 px-2 py-1.5">Last name</th>
                  <th className="text-left text-[10px] font-semibold text-gray-600 dark:text-gray-300 px-2 py-1.5">Email</th>
                  <th className="text-left text-[10px] font-semibold text-gray-600 dark:text-gray-300 px-2 py-1.5">Date</th>
                  <th className="text-left text-[10px] font-semibold text-gray-600 dark:text-gray-300 px-2 py-1.5">Time</th>
                  <th className="w-6"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => {
                  const err = rowErrors[idx];
                  const cellCls = (bad: boolean) =>
                    `w-full px-1.5 py-1 text-[10px] focus:outline-none border-0 ${
                      bad
                        ? "bg-red-50 text-red-700 ring-1 ring-inset ring-red-300"
                        : "bg-transparent focus:bg-indigo-50/40"
                    }`;
                  return (
                    <tr
                      key={idx}
                      className="border-b border-gray-100 dark:border-gray-800 last:border-0 hover:bg-gray-50/40"
                    >
                      <td className="px-2 py-0.5 text-[#7B7B7B] font-semibold dark:text-gray-500 text-center align-middle">
                        {idx + 1}
                      </td>
                      <td className="border-l border-gray-100 dark:border-gray-800">
                        <input
                          type="text"
                          placeholder="+234..."
                          value={row.phone}
                          onChange={(e) => setCell(idx, "phone", e.target.value)}
                          className={cellCls(err.phone)}
                        />
                      </td>
                      <td className="border-l border-gray-100 dark:border-gray-800">
                        <input
                          type="text"
                          placeholder="First"
                          value={row.first_name}
                          onChange={(e) => setCell(idx, "first_name", e.target.value)}
                          className={cellCls(err.first_name)}
                        />
                      </td>
                      <td className="border-l border-gray-100 dark:border-gray-800">
                        <input
                          type="text"
                          placeholder="Last"
                          value={row.last_name}
                          onChange={(e) => setCell(idx, "last_name", e.target.value)}
                          className={cellCls(err.last_name)}
                        />
                      </td>
                      <td className="border-l border-gray-100 dark:border-gray-800">
                        <input
                          type="text"
                          placeholder="email@..."
                          value={row.email}
                          onChange={(e) => setCell(idx, "email", e.target.value)}
                          className={cellCls(err.email)}
                        />
                      </td>
                      <td className="border-l border-gray-100 dark:border-gray-800">
                        <input
                          type="text"
                          placeholder="dd-mm-yyyy"
                          value={row.appointment_date}
                          onChange={(e) => setCell(idx, "appointment_date", e.target.value)}
                          className={cellCls(err.appointment_date)}
                        />
                      </td>
                      <td className="border-l border-gray-100 dark:border-gray-800">
                        <input
                          type="text"
                          placeholder="HH:MM"
                          value={row.appointment_time}
                          onChange={(e) => setCell(idx, "appointment_time", e.target.value)}
                          className={cellCls(err.appointment_time)}
                        />
                      </td>
                      <td className="border-l border-gray-100 dark:border-gray-800 px-1 text-center align-middle">
                        <button
                          type="button"
                          onClick={() => removeRow(idx)}
                          className="text-gray-300 dark:text-gray-600 hover:text-red-500 inline-flex items-center"
                          title="Remove row"
                        >
                          <Trash2 size={11} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <button
            type="button"
            onClick={addRow}
            className="mt-2 w-full flex items-center justify-center gap-1.5 text-[11px] text-indigo-500 hover:text-indigo-700 border border-dashed border-indigo-300 rounded-lg py-1.5 hover:border-indigo-400 transition-colors"
          >
            <Plus size={12} /> Add row
          </button>

          <button
            type="button"
            onClick={submitManualRows}
            disabled={uploading || !canSubmit}
            className={`mt-3 w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
              canSubmit && !uploading
                ? "bg-emerald-500 hover:bg-emerald-600 text-white"
                : "bg-gray-200 dark:bg-gray-700 text-[#7B7B7B] font-semibold dark:text-gray-500 cursor-not-allowed"
            }`}
          >
            <Upload size={14} />
            {uploading
              ? "Submitting..."
              : canSubmit
              ? `Submit (${touchedRowCount} customer${touchedRowCount === 1 ? "" : "s"})`
              : touchedRowCount === 0
              ? "Fill in at least 1 row"
              : "Fix errors above"}
          </button>
        </div>

        {/* Variables & Preview */}
        {selectedTemplate && (
          <>
            {variableSlots.length > 0 && (
              <div className="mb-4">
                <FieldLabel>Variable Mapping</FieldLabel>
                <div className="space-y-2 mt-2">
                  {variableSlots.map((idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <span className="font-mono text-xs bg-indigo-50 text-indigo-600 px-2 py-1 rounded w-10 text-center">
                        {`{{${idx}}}`}
                      </span>
                      <span className="text-gray-300 dark:text-gray-600">→</span>
                      <input
                        type="text"
                        value={getDisplayValue(variables[idx] || "")}
                        onChange={(e) => setVariableValue(idx, e.target.value)}
                        placeholder={`Value for {{${idx}}}`}
                        className="flex-1 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-1.5 text-xs focus:ring-1 focus:ring-indigo-300"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── WhatsApp-style preview (Sree 06-29 spec: "there should be
                a place where they can preview it"). Mirrors the Send
                Message card preview so the experience is consistent
                across all action cards. */}
            <div className="mb-4">
              <FieldLabel>Preview</FieldLabel>
              <div className="rounded-xl bg-[#e5ddd5] p-3 mt-1.5">
                <div className="flex items-center gap-2 mb-2 px-1">
                  <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center text-white text-[10px] font-bold">
                    B
                  </div>
                  <span className="text-[11px] font-semibold text-gray-700 dark:text-gray-300">
                    Your Business
                  </span>
                </div>
                <div className="bg-white dark:bg-gray-900 rounded-lg rounded-tl-sm shadow-sm overflow-hidden max-w-[230px]">
                  {selectedTemplate.header_type === "image" && selectedTemplate.header_content && (
                    <img
                      src={selectedTemplate.header_content}
                      alt="Template header"
                      className="w-full h-24 object-cover"
                      onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                    />
                  )}
                  <div className="px-2.5 py-2">
                    <p className="text-[11px] text-gray-800 dark:text-gray-200 whitespace-pre-line leading-snug">
                      {buildFinalMessage(selectedTemplate.content ?? "", variables) || "(no body)"}
                    </p>
                    <p className="text-[9px] text-[#7B7B7B] font-semibold dark:text-gray-500 text-right mt-1">10:30 AM ✓✓</p>
                  </div>
                </div>
                {selectedTemplate.quick_reply_buttons?.length ? (
                  <div className="mt-1 space-y-0.5 max-w-[230px]">
                    {selectedTemplate.quick_reply_buttons.map((btn: any) => (
                      <div
                        key={btn.id ?? btn.label}
                        className="w-full text-center text-[11px] text-emerald-700 bg-white dark:bg-gray-900 rounded-md py-1.5 shadow-sm"
                      >
                        {btn.label}
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          </>
        )}

        {/* Quick Reply Buttons */}
        <ReplyButtons
          buttons={replyButtons}
          selectedId={selectedButtonId}
          onSelect={(id) => {
            setSelectedButtonId(id);
            // Auto-spawn the branch's follow-up card config when the operator
            // clicks Yes / No / Reschedule. Default templates per branch
            // (matches Joseph's June 17 spec):
            //   Yes        → no follow-up (just Assign Agent at end of flow)
            //   No         → appointment_cancelled (terminal — no agent)
            //   Reschedule → appointment_reschedule (then Assign Agent)
            // Operator can override the template via the inline preview
            // below (clicking it expands into the full Send Message card).
            const btn = replyButtons.find((b) => b.id === id);
            const label = btn?.label?.toLowerCase() ?? "";
            const branches = { ...(config.branches ?? {}) };
            if (label === "yes") {
              // YES → no follow-up template; just routes to agent
              branches[id] = { template: null, action: "assign_agent" };
            } else if (label === "no") {
              branches[id] = { template: "appointment_cancelled", action: "send" };
            } else if (label === "reschedule") {
              branches[id] = { template: "appointment_reschedule", action: "send" };
            } else if (!branches[id]) {
              // Custom buttons added by the operator default to no template —
              // they configure manually in the preview below.
              branches[id] = { template: "", action: "send" };
            }
            update({ selectedButtonId: id, branches });
          }}
          onAdd={addButton}
          onRemove={removeButton}
          onLabelChange={updateLabel}
        />

        {/* Branch preview — shows the auto-spawned follow-up card for the
            currently selected reply button. Mirrors the June 17 design:
            "If I click on Reschedule, automatically shows another action
            card... with appointment_reschedule template auto-selected."
            Only renders when a button is selected AND has a follow-up
            action defined. */}
        {selectedButtonId && (() => {
          const btn = replyButtons.find((b) => b.id === selectedButtonId);
          const branch = config.branches?.[selectedButtonId];
          if (!btn || !branch) return null;

          // YES branch — no message, just agent handoff. Render a slim
          // notice instead of a full card preview.
          if (branch.action === "assign_agent") {
            return (
              <div className="mt-3 rounded-lg border border-indigo-100 bg-indigo-50/40 p-3">
                <p className="text-[10px] font-medium text-indigo-500 mb-0.5">
                  When customer clicks "{btn.label}"
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-300">
                  → Assigns directly to agent (no follow-up message)
                </p>
              </div>
            );
          }

          // NO / Reschedule / custom — render a Send Message card preview
          // with the branch's template pre-picked.
          const branchTemplate = availableTemplates.find(
            (t) => t.template_name === branch.template
          );
          const isApproved = !!branchTemplate;
          // Fallback button list — Meta has the templates APPROVED with
          // these exact buttons (verified 06-29). If the CRM /templates/
          // endpoint doesn't return quick_reply_buttons for one of them
          // we still want the preview to show the real customer-facing
          // buttons. Stays in sync with submit_appointment_templates.sh.
          const FALLBACK_BUTTONS: Record<string, string[]> = {
            appointment_reschedule: ["Tomorrow", "This week", "Next week"],
            appointment_cancel_reason: [
              "Different time slot",
              "Not interested",
              "Need assistance",
            ],
            appointment_missed: ["Reschedule", "Cancel"],
          };
          const branchButtons =
            branchTemplate?.quick_reply_buttons?.length
              ? branchTemplate.quick_reply_buttons.map((b: any) => ({ label: b.label }))
              : (FALLBACK_BUTTONS[branch.template] ?? []).map((label) => ({ label }));
          return (
            <div className="mt-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-3">
              <p className="text-[10px] font-medium text-indigo-500 mb-1.5">
                When customer clicks "{btn.label}" → Send Message
              </p>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-700 dark:text-gray-300 truncate">
                  Template: <span className="font-medium">{branch.template || "—"}</span>
                </span>
                {!isApproved && branch.template && (
                  <span className="text-[10px] text-amber-600 ml-1">
                    (pending Meta approval)
                  </span>
                )}
              </div>
              {branchTemplate && (
                <>
                  {branchTemplate.header_type === "image" && branchTemplate.header_content && (
                    <div className="w-full h-20 bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden mb-2">
                      <img
                        src={branchTemplate.header_content}
                        alt="Branch template header"
                        className="w-full h-full object-cover"
                        onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                      />
                    </div>
                  )}
                  <div className="text-[11px] text-gray-600 dark:text-gray-300 whitespace-pre-line bg-gray-50 dark:bg-gray-800 rounded p-2 mb-2">
                    {branchTemplate.content || "(no body)"}
                  </div>
                  {/* Render the branch template's quick-reply buttons.
                      Without this, picking "Reschedule" only showed the body
                      text — never the Tomorrow / This week / Next week
                      buttons the customer will actually see. Falls back
                      to a hardcoded map (FALLBACK_BUTTONS) if the CRM
                      /templates/ endpoint doesn't return them. (Joseph 06-29). */}
                  {branchButtons.length ? (
                    <div className="space-y-1">
                      {branchButtons.map((b, i) => (
                        <div
                          key={`${b.label}-${i}`}
                          className="w-full text-center text-[11px] text-indigo-500 bg-indigo-50 rounded py-1.5"
                        >
                          {b.label}
                        </div>
                      ))}
                    </div>
                  ) : null}
                </>
              )}
              {!branchTemplate && branch.template && (
                <>
                  <div className="text-[11px] text-[#7B7B7B] font-semibold dark:text-gray-500 italic bg-gray-50 dark:bg-gray-800 rounded p-2 mb-2">
                    Template body previews here once "{branch.template}" loads
                    from the CRM. The customer-facing buttons are shown below.
                  </div>
                  {/* Even without branchTemplate (CRM /templates/ endpoint
                      didn't return this template), show fallback buttons so
                      the operator sees what the customer will see. */}
                  {branchButtons.length ? (
                    <div className="space-y-1">
                      {branchButtons.map((b, i) => (
                        <div
                          key={`${b.label}-${i}`}
                          className="w-full text-center text-[11px] text-indigo-500 bg-indigo-50 rounded py-1.5"
                        >
                          {b.label}
                        </div>
                      ))}
                    </div>
                  ) : null}
                </>
              )}
            </div>
          );
        })()}
    </CardShell>
  );
};